import { ItemMenuCategoryDto, ItemMenuItemDetailsDto } from '@/core';
import {
  CategoryState,
  useGetCategoriesQuery,
  useGetItemMenuDetailsQuery,
} from '@/redux';
import { array } from '@/utils';
import { useMemo } from 'react';
import {
  convertItemMenuItemsToTreeTableData,
  ItemMenuTreeTableNode,
} from './convertItemMenuItemsToTreeTableData';

interface Props {
  id: string;
  communityId: string;
}

export function useTreeTableData(props: Props) {
  const { id, communityId } = props;
  const { data } = useGetItemMenuDetailsQuery({ id, listed: null });
  const { items = array.empty<ItemMenuItemDetailsDto>() } = data ?? {};
  const { categories = array.empty<ItemMenuCategoryDto>() } = data ?? {};
  const { data: allCategories = array.empty<CategoryState>() } =
    useGetCategoriesQuery({ communityId, listed: true });

  return useMemo(() => {
    const data = convertItemMenuItemsToTreeTableData(
      items,
      categories,
      allCategories,
    );
    const itemMenuCategories = extractItemMenuCategories(data);
    return { data, itemMenuCategories };
  }, [allCategories, categories, items]);
}

export const extractItemMenuCategories = (
  nodes: ItemMenuTreeTableNode[],
): ItemMenuCategoryDto[] => {
  return nodes.reduce<ItemMenuCategoryDto[]>((categories, node) => {
    if (!node.item && !node.itemDescription) {
      categories.push({
        categoryId: node.id,
        min: node.min!,
        max: node.max!,
        rootId: node.category?.id,
        parentId: node.subCategory1?.id,
        explicit: node.explicit ?? false,
      });
    }
    if (node.children) {
      return categories.concat(extractItemMenuCategories(node.children));
    }
    return categories;
  }, []);
};

export const extractItemMenuItems = (
  nodes: ItemMenuTreeTableNode[],
): string[] => {
  return nodes.reduce<string[]>((items, node) => {
    if (node.item && !node.itemDescription) {
      (node.subCategory2?.id || node.subCategory1?.id || node.category?.id) &&
        items.push(
          node.subCategory2?.id ?? node.subCategory1?.id ?? node.category!.id,
        );
    }
    if (node.children) {
      return items.concat(extractItemMenuItems(node.children));
    }
    return items;
  }, []);
};
